import {
    useQuery,
    useMutation,
    useQueryClient,
    useInfiniteQuery,
} from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { usersApi } from '@wtf/shared/api/users'
import * as UserTypes from '@wtf/shared'
import { batchNormalizeCollections } from '@wtf/shared'
import { normalizeLook } from '@wtf/shared'
import { NormalizedProfileDto } from '@wtf/shared'
import { userCollectionsQueryOptions } from '@/hooks/queries/userCollectionsQueryOptions'
import { replaceImageDomain } from '@/utils/replaceImageDomain'
/**
 * Hook to fetch users with pagination
 */
export function useUsers(params: UserTypes.GetUsersParams = {}) {
    return useQuery({
        queryKey: ['users', params],
        queryFn: () => usersApi.getUsers(params),
        staleTime: 5 * 60 * 1000, // 5 minutes
    })
}

/**
 * Hook to update a user profile
 */
export function useUpdateUser() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: UserTypes.UpdateUserRequest
        }) => usersApi.updateUser(id, data),
        onSuccess: (updatedUser) => {
            const cachedProfile =
                queryClient.getQueryData<NormalizedProfileDto>([
                    'user',
                    'profile',
                    updatedUser.id,
                ])

            if (cachedProfile) {
                queryClient.setQueryData<NormalizedProfileDto>(
                    ['user', 'profile', updatedUser.id],
                    {
                        ...cachedProfile,
                        // Update only user-editable fields from API response
                        coverUrl: updatedUser.coverUrl,
                        avatarUrl: updatedUser.avatarUrl,
                        name: updatedUser.name,
                        bio: updatedUser.bio,
                        website: updatedUser.website,
                        // Preserve normalized fields: publicCollectionIds, privateAndSavedCollectionIds
                    }
                )
            }

            // Still invalidate users list for any other views
            queryClient.invalidateQueries({ queryKey: ['users'] })
        },
    })
}

/**
 * Hook to request account deletion
 */
export function useRequestAccountDeletion() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
            usersApi.requestAccountDeletion(id, reason),
        onSuccess: (_, { id }) => {
            // Invalidate user profile to refetch with deletion request
            queryClient.invalidateQueries({ queryKey: ['user', 'profile', id] })
        },
    })
}

/**
 * Hook to cancel account deletion request
 */
export function useCancelAccountDeletion() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (id: string) => usersApi.cancelAccountDeletion(id),
        onSuccess: (_, id) => {
            // Invalidate user profile to refetch without deletion request
            queryClient.invalidateQueries({ queryKey: ['user', 'profile', id] })
        },
    })
}

/**
 * Hook to get account deletion status
 */
export function useAccountDeletionStatus(id: string | undefined) {
    return useQuery({
        queryKey: ['user', 'deletion-request', id],
        queryFn: () => usersApi.getAccountDeletionStatus(id!),
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: false, // Don't retry if no deletion request found (404)
    })
}

/**
 * Hook to get a user's profile
 */
export function useUserProfile(id: string | undefined) {
    const queryClient = useQueryClient()
    return useQuery<NormalizedProfileDto>({
        queryKey: ['user', 'profile', id],
        queryFn: () => loadUserProfile(id!, queryClient),
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
    })
}

/**
 * Hook to get users that a user subscribed to (followers) with infinite scrolling
 */
export function useUserSubscribersInfinite(
    id: string | undefined,
    limit: number = 10
) {
    const queryClient = useQueryClient()
    return useInfiniteQuery({
        queryKey: ['user', id, 'subscribers', 'infinite'],
        queryFn: async ({ pageParam = 1 }) => {
            const result = await usersApi.getUserSubscribers(id!, {
                page: pageParam,
                limit,
            })
            result.subscribers.forEach((user) => {
                queryClient.setQueryData(['user', 'summary', user.id], {
                    ...user,
                    avatarUrl: user.avatarUrl
                        ? replaceImageDomain(user.avatarUrl)
                        : user.avatarUrl,
                })
            })
            return result
        },
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
        getNextPageParam: (lastPage) => {
            const { page, pages } = lastPage.pagination
            return page < pages ? page + 1 : undefined
        },
        initialPageParam: 1,
    })
}

/**
 * Hook to get users that a user subscribes to (following) with infinite scrolling
 */
export function useUserSubscribedToInfinite(
    id: string | undefined,
    limit: number = 10
) {
    const queryClient = useQueryClient()
    return useInfiniteQuery({
        queryKey: ['user', id, 'subscribedTo', 'infinite'],
        queryFn: async ({ pageParam = 1 }) => {
            const result = await usersApi.getUserSubscribedTo(id!, {
                page: pageParam,
                limit,
            })
            result.subscriptions.forEach((user) => {
                queryClient.setQueryData(['user', 'summary', user.id], {
                    ...user,
                    avatarUrl: user.avatarUrl
                        ? replaceImageDomain(user.avatarUrl)
                        : user.avatarUrl,
                })
            })
            return result
        },
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
        getNextPageParam: (lastPage) => {
            const { page, pages } = lastPage.pagination
            return page < pages ? page + 1 : undefined
        },
        initialPageParam: 1,
    })
}

/**
 * Hook to check if a username is available
 */
export function useCheckUsernameAvailability() {
    return useMutation({
        mutationFn: (username: string) =>
            usersApi.checkUsernameAvailability(username),
    })
}

/**
 * Hook to get all products created by a user (only accessible by the user themselves)
 */
export function useUserProducts(
    id: string | undefined,
    params: { page?: number; limit?: number } = {}
) {
    const queryClient = useQueryClient()
    return useQuery({
        queryKey: ['user', id, 'products', params],
        queryFn: async () => {
            const result = await usersApi.getUserProducts(id!, params)
            result.products.forEach((product) => {
                queryClient.setQueryData(['product', product.id], product)
            })
            return result
        },
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
    })
}

/**
 * Hook to get all looks created by a user (only accessible by the user themselves)
 */
export function useUserLooks(
    id: string | undefined,
    params: { page?: number; limit?: number } = {}
) {
    const queryClient = useQueryClient()
    return useQuery({
        queryKey: ['user', id, 'looks', params],
        queryFn: async () => {
            const result = await usersApi.getUserLooks(id!, params)
            result.looks.forEach((look) => {
                const normalized = normalizeLook(queryClient, look)
                queryClient.setQueryData(['look', look.id], normalized)
            })
            return result
        },
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
    })
}

/**
 * Hook to get all collections created by a user (only accessible by the user themselves)
 */
export function useUserCollections(
    id: string | undefined,
    params: { page?: number; limit?: number } = {}
) {
    const queryClient = useQueryClient()
    return useQuery(userCollectionsQueryOptions(queryClient, id, params))
}

/**
 * Hook to get a user's summary (public info only)
 */
export function useUserSummary(id: string | undefined) {
    return useQuery({
        queryKey: ['user', 'summary', id],
        queryFn: async () => {
            const summary = await usersApi.getUserSummary(id!)
            return {
                ...summary,
                avatarUrl: summary.avatarUrl
                    ? replaceImageDomain(summary.avatarUrl)
                    : summary.avatarUrl,
            }
        },
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutes
    })
}

/**
 * Shared loader for user profile data, used by both React Query hook and route loader.
 */
export async function loadUserProfile(
    id: string,
    queryClient: QueryClient
): Promise<NormalizedProfileDto> {
    const profile = await usersApi.getUserProfile(id)
    // Prime the user summary cache
    queryClient.setQueryData<UserTypes.UserSummaryDto>(
        ['user', 'summary', id],
        {
            id,
            username: profile.username,
            name: profile.name,
            bio: profile.bio,
            avatarUrl: profile.avatarUrl
                ? replaceImageDomain(profile.avatarUrl)
                : profile.avatarUrl,
            website: profile.website,
            role: profile.role,
        }
    )
    // Batch normalize and cache all collections
    const allCollections = [
        ...profile.publicCollections,
        ...profile.privateAndSavedCollections,
    ]
    const normalizedCollections = batchNormalizeCollections(
        queryClient,
        allCollections
    )
    normalizedCollections.forEach((normalized) => {
        queryClient.setQueryData(['collection', normalized.id], normalized)
    })
    // Extract IDs
    const publicCollectionIds = profile.publicCollections.map((c) => c.id)
    const privateAndSavedCollectionIds = profile.privateAndSavedCollections.map(
        (c) => c.id
    )
    // Return flattened profile data
    const { publicCollections, privateAndSavedCollections, ...rest } = profile
    return {
        ...rest,
        publicCollectionIds,
        privateAndSavedCollectionIds,
    }
}
